import React, { useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { MdOutlineDeleteOutline } from "react-icons/md";

const BarVisualization = ({
    indicators,
    locations,
    onSubmit, // Handler from parent
    // Receive all states as props from parent (NO MORE useState!)
    localSelectedLocations,
    setLocalSelectedLocations,
    useTemproBarChart,
    barChartType,
    setBarChartType,
    barIndicators,
    setBarIndicators
}) => {
    // Redux selector for bar config (for restoration only)
    const barConfig = useSelector((state) => state.mapConfig.barConfig);

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    const noOptionsMessage = () => "No compatible indicators found in your workspace.";

    // Handle add indicator
    const handleAddIndicator = () => {
        setBarIndicators([
            ...barIndicators,
            { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null, chartType: null }
        ]);
    };

    // Handle chart type change
    const handleChartTypeChange = (index, selectedOption) => {
        const newIndicators = [...barIndicators];
        newIndicators[index].chartType = selectedOption;
        setBarIndicators(newIndicators);
    };

    // Handle indicator change
    const handleIndicatorChange = (index, selectedOption) => {
        const newIndicators = [...barIndicators];
        newIndicators[index].colorIndicator = selectedOption;
        newIndicators[index].colorSubIndicators = selectedOption?.subIndicators || [];
        newIndicators[index].selectedColorSubIndicator = null;
        setBarIndicators(newIndicators);
    };

    // Handle sub indicator change
    const handleSubIndicatorChange = (index, selectedOption) => {
        const newIndicators = [...barIndicators];
        newIndicators[index].selectedColorSubIndicator = selectedOption;
        setBarIndicators(newIndicators);
    };
    useEffect(() => {
        if (!locations || locations.length === 0) return;

        const validOptions = [
            {
                label: `All States / UTs of India`,
                value: 1000, geo_level: 1, type: "Children"
            },
            ...locations.map(f => ({
                value: f.properties?.code,
                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4)})`,
                geo_level: f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4,
                type: ""
            }))
        ];

        // Filter out any selected locations that no longer exist
        const filteredSelected = localSelectedLocations.filter(selected =>
            validOptions.some(opt => opt.value === selected.value && opt.type === selected.type)
        );

        if (filteredSelected.length !== localSelectedLocations.length) {
            setLocalSelectedLocations(filteredSelected);
        }
    }, [locations]);

    // Handle reset
    const handleReset = () => {
        setLocalSelectedLocations([]);
        setBarChartType("column");
        setBarIndicators([
            { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null, chartType: null }
        ]);
    };

    // Data patch/restoration logic using useEffect to read from Redux barConfig
    useEffect(() => {
        if (!barConfig) return;
        if (barConfig?.indicators?.length > 0) {
            if (barConfig.selectedLocations) {
                setLocalSelectedLocations(barConfig.selectedLocations);
            }
            if (barConfig.barChartType) {
                setBarChartType(barConfig.barChartType);
            }
            const patchedIndicators = barConfig.indicators.map((ind) => {
                const matched = indicators.find((f) => f.indicator_code === ind.i_code);
                return {
                    colorIndicator: matched
                        ? {
                            value: matched.indicator_code,
                            label: matched.indicator_name,
                            subIndicators: matched.sub_indicators || [],
                        }
                        : { value: ind.i_code, label: ind.i_code },
                    colorSubIndicators: matched?.sub_indicators || [],
                    selectedColorSubIndicator: ind.si_code
                        ? {
                            value: ind.si_code,
                            label: matched?.sub_indicators.find((s) => s.code === ind.si_code)?.name || ind.si_code,
                        }
                        : null,
                    chartType: ind.chartType
                        ? { value: ind.chartType, label: ind.chartType === "bar" ? "Column" : useTemproBarChart ? "Dot" : "Dot" }
                        : { value: "bar", label: "Column" },
                };
            });

            setBarIndicators(patchedIndicators);
        }
    }, [barConfig, indicators]);
    // Filter bar indicators that are no longer available in the current workspace
    useEffect(() => {
        if (!barIndicators || barIndicators.length === 0) return;
        if (!indicators || indicators.length === 0) return;

        const availableIndicatorCodes = new Set(indicators.map(i => i.indicator_code));

        const filteredIndicators = barIndicators.filter(ind => {
            // keep if no indicator selected yet OR indicator still available
            if (!ind.colorIndicator) return true;
            return availableIndicatorCodes.has(ind.colorIndicator.value);
        });

        // only update if something actually changed
        if (filteredIndicators.length !== barIndicators.length) {
            setBarIndicators(filteredIndicators);
        }
    }, [indicators, barIndicators]);


    // Submit functionality - NO API CALL, just prepare data and call parent handler
    const handleBarSubmit = () => {
        const lgdDatas = localSelectedLocations.map(item => ({
            code: item.value,
            geo_level_code: item.geo_level,
            type: item.type || ""
        }));

        const formattedIndicators = barIndicators.map((ind) => ({
            i_code: ind.colorIndicator?.value || "",
            si_code: ind.selectedColorSubIndicator?.value || "",
            chartType: ind.chartType?.value || ""
        }));

        const payload = {
            lgd_codes: lgdDatas,
            indicators: formattedIndicators,
        };

        const formattedPayload = {
            lgd_codes: lgdDatas,
            barChartType: barChartType,
            indicators: formattedIndicators,
            selected_Location: localSelectedLocations,
            barindicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        const reduxPayload = {
            visualizationType: "bar",
            barChartType: barChartType,
            selectedLocations: localSelectedLocations,
            indicators: formattedIndicators,
        };

        // Call parent submit handler with prepared data
        onSubmit({ payload, formattedPayload, reduxPayload });
    };

    // Check if submit should be disabled
    const isBarSubmitDisabled = !(
        barIndicators.some(ind => ind.colorIndicator) &&
        Array.isArray(localSelectedLocations) &&
        localSelectedLocations.length > 0
    );
    return (
        <div className="col-md-12 mt-3">
            <div className="cardbg p-3">
                <Form.Label>Column Chart Configuration</Form.Label>
                <div className="col-md-6">
                    {/* Chart Type (Radio buttons) */}
                    <Form.Group className="mb-3">
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Column"
                                name="chartType"
                                value="column"
                                checked={barChartType === "column"}
                                onChange={(e) => setBarChartType(e.target.value)}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="Bar"
                                name="chartType"
                                value="bar"
                                checked={barChartType !== "column"}
                                onChange={(e) => setBarChartType(e.target.value)}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Select
                            isMulti
                            options={[
                                {
                                    label: `All States / UTs of India`,
                                    value: 1000, geo_level: 1, type: "Children"
                                },
                                ...(locations.length > 0
                                    ? locations.flatMap((f) => {
                                        const name = f.name || f.properties?.name;
                                        const code = f.properties?.code || f.id;
                                        const geoLevel = f?.geo_level_code || f.geo_level || 4;
                                        const type = "Children"
                                        if (geoLevel === 2) {
                                            return [
                                                {
                                                    label: `All Districts of ${name}`,
                                                    value: code,
                                                    geo_level: 2,
                                                    type
                                                },
                                            ];
                                        }
                                        if (geoLevel === 3) {
                                            return [
                                                {
                                                    label: `All ULG of ${name}`,
                                                    value: code,
                                                    geo_level: 3,
                                                    type
                                                },
                                            ];
                                        }
                                        return [];
                                    })
                                    : []),
                                {
                                    label: "India (National)",
                                    value: 1000, geo_level: 1
                                },
                                ...locations.map((f) => ({
                                    value: f.properties?.code || f.id,
                                    label: `${f.name || f.properties?.name} (${geoLevelLabel(f?.geo_level_code || f.geo_level || 4)})`,
                                    geo_level: f.properties?.geo_level_code || f.geo_level || f.geo_level_code,
                                    type: ""
                                })),
                            ]}
                            onChange={setLocalSelectedLocations}
                            value={localSelectedLocations}
                            placeholder="Choose Location"
                            getOptionValue={(e) => `${e.value}-${e.type}`}
                            getOptionLabel={(e) => e.label}
                            isSearchable={true}
                            filterOption={(option, inputValue) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
    }
                        />
                    </Form.Group>
                </div>
                {barIndicators.map((ind, idx) => (
                    <div key={idx} className="mb-3 p-3 border rounded">
                        <Form.Label>Indicator {idx + 1}</Form.Label>
                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group>
                                    <Select
                                        options={indicators.filter(f => f.measurement_type !== "Categorical - Nominal - Text").map((f) => ({
                                            value: f.indicator_code,
                                            label: `${f.indicator_name} (${f.indicator_date})`,
                                            subIndicators: f.sub_indicators || [],
                                        }))}
                                        onChange={(opt) => handleIndicatorChange(idx, opt)}
                                        value={ind.colorIndicator}
                                        classNamePrefix="select"
                                        placeholder="Choose Indicator"
                                        noOptionsMessage={noOptionsMessage}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-3">
                                {ind.colorIndicator && (
                                    <Form.Group>
                                        <Select
                                            options={[
                                                { value: "bar", label: "Column" },
                                                { value: "line", label: useTemproBarChart ? "Dot" : "Dot" }
                                            ]}
                                            value={ind.chartType || { value: "bar", label: "Column" }}
                                            onChange={(opt) => handleChartTypeChange(idx, opt)}
                                            placeholder="Chart Type"
                                            classNamePrefix="select"
                                            noOptionsMessage={noOptionsMessage}
                                        />
                                    </Form.Group>
                                )}
                            </div>

                            <div className="col-md-4">
                                {ind.colorIndicator && ind.colorSubIndicators?.length > 0 && (
                                    <Form.Group>
                                        <Select
                                            options={ind.colorSubIndicators.map((s) => ({
                                                value: s.code,
                                                label: s.name,
                                            }))}
                                            value={ind.selectedColorSubIndicator}
                                            onChange={(opt) => handleSubIndicatorChange(idx, opt)}
                                            placeholder="Choose Sub Indicator"
                                            classNamePrefix="select"
                                            noOptionsMessage={noOptionsMessage}
                                        />
                                    </Form.Group>
                                )}
                            </div>
                            <div className="col-md-1 ui-mb-3">
                                {ind.colorIndicator && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => {
                                            const newIndicators = [...barIndicators];
                                            newIndicators.splice(idx, 1);
                                            setBarIndicators(newIndicators);
                                        }}
                                        title="Remove Indicator"
                                    >
                                        <MdOutlineDeleteOutline />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                <Button variant="outline-primary" onClick={handleAddIndicator}>
                    + Add Indicator
                </Button>
                <div className="row mt-2">
                    <div className="col-md-8 text-start"><p style={{ 'fontSize': '0.85em' }}> <i>Note:</i> Indicators added to the workspace are listed in the dropdown list. To include additional indicators, go to the Home page and add the required indicators to the workspace.</p></div>
                    <div className="text-end col-md-4">
                        <Button variant="secondary" onClick={handleReset} className="btn-sm">
                            Reset
                        </Button>&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button onClick={handleBarSubmit} className="btn-sm btn-primary" disabled={isBarSubmitDisabled}>
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BarVisualization;